import "@app/App.css";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Dashboard } from "@dto/Dashboard";
import { updateStorageSelection } from "@utils/localStorage";
import { TW_DashboardMenuButton, TW_DashboardMenuButtonIcon } from "@utils/twClasses";
import { EllipsisVertical } from "lucide-react";
import { useState } from "react";
import { useModelStore } from "src/store";
import DashboardMenuDeleteItem from "./DashboardMenuDeleteItem";
import DashboardMenuDuplicateItem from "./DashboardMenuDuplicateItem";
import DashboardMenuRenameItem from "./DashboardMenuRenameItem";
import DashboardMenuShareItem from "./DashboardMenuShareItem";

interface DashboardMenuButtonProps {
  dashboard: Dashboard;
  showIcon: boolean;
}

function DashboardMenuButton({ dashboard, showIcon }: DashboardMenuButtonProps) {

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const allDashboards = useModelStore((state) => state.dashboards);
  const selectedDashboard = useModelStore((state) => state.selectedDashboard);
  const selectDashboard = useModelStore((state) => state.selectDashboard);
  const setDashboards = useModelStore((state) => state.updateDashboards);
  const setSelectedDashboard = useModelStore((state) => state.updateSelectedDashboard);
  const workspace = useModelStore((state) => state.selectedWorkspace);

  const sortDashboards = (dashboards: Dashboard[]) => {
    return [...dashboards].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  const deleteDashboardFromState = () => {
    const sortedDashboards = sortDashboards(allDashboards ?? []);
    const newDashboards = sortedDashboards.filter(d => d._key !== dashboard._key);

    let newSelectedDashboard = selectedDashboard;
    if (newSelectedDashboard?._key === dashboard._key) {
      let dashboardIndex = sortedDashboards.findIndex(d => d._key === dashboard._key);
      if (newDashboards.length > 0 && dashboardIndex > -1) {
        newSelectedDashboard = newDashboards[Math.max(0, dashboardIndex - 1)];
      } else {
        newSelectedDashboard = null;
      }
    }
    setDashboards(newDashboards);
    setSelectedDashboard(newSelectedDashboard);
    updateStorageSelection(workspace?._key, null, null, newSelectedDashboard?._key);
  }


  const updateDashboard = (dashboard: Dashboard) => {
    const newDashboards = (allDashboards ?? []).map((currentDashboard: Dashboard) => currentDashboard._key === dashboard._key ? dashboard : currentDashboard);
    if (selectedDashboard?._key === dashboard._key) {
      setSelectedDashboard(dashboard);
    } 
    setDashboards(newDashboards);
  }

  const dashboardUpdated = (dashboard: Dashboard) => {
    updateDashboard(dashboard);
    setIsPopoverOpen(false);
  }

  const deleteDashboard = () => {
    deleteDashboardFromState();
    setIsPopoverOpen(false);
  }

  const closeMenu = () => {
    setIsPopoverOpen(false);
  }

  function openMenu(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    setIsPopoverOpen(true);
  }

  const duplicateDashboard = (newDashboard: Dashboard) => {
    const newDashboards = sortDashboards([...(allDashboards || []), newDashboard]);
    setDashboards(newDashboards);
    selectDashboard(newDashboard);
    setSelectedDashboard(newDashboard);
    updateStorageSelection(workspace?._key, null, null, newDashboard._key);
  }
  
  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger className={TW_DashboardMenuButton} onClick={openMenu}>
        <EllipsisVertical className={TW_DashboardMenuButtonIcon + (showIcon ? " text-gray-400" : " text-gray-100")} />
      </PopoverTrigger>
      <PopoverContent className="w-max p-1">
        <ul>
          <li><DashboardMenuRenameItem dashboard={dashboard} onDashboardUpdate={dashboardUpdated} closeMenu={closeMenu} /></li>
          <li><DashboardMenuDuplicateItem dashboard={dashboard} onDashboardUpdate={duplicateDashboard} closeMenu={closeMenu} /></li>
          <li><DashboardMenuDeleteItem dashboard={dashboard} onDashboardDelete={deleteDashboard} closeMenu={closeMenu} /></li>
          <li><DashboardMenuShareItem dashboard={dashboard} onDashboardUpdate={dashboardUpdated} closeMenu={closeMenu} /></li>
        </ul>
      </PopoverContent>
    </Popover>
  )
}

export default DashboardMenuButton;
